import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { User, Lock, AlertCircle, Eye, EyeOff, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

interface LoginForm {
    emailOrUsername: string;
    password: string;
}

export default function Login(): React.JSX.Element {
    const navigate = useNavigate();
    const { login } = useAuth();
    const { t } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<LoginForm>({ emailOrUsername: '', password: '' });
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        setError('');
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setError('');
        if (!formData.emailOrUsername || !formData.password) {
            setError(t.loginFillFields);
            return;
        }
        setLoading(true);
        try {
            await login(formData.emailOrUsername, formData.password);
            toast.success(t.loginSuccess, { description: t.loginSuccessDesc });
            navigate('/');
        } catch (err) {
            const msg = (err as Error).message;
            setError(msg);
            toast.error(t.loginFailed, { description: msg });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col" style={{ background: 'hsl(32 38% 97%)' }}>

            {/* ── Top brand band ── */}
            <div
                className="relative flex-shrink-0 flex flex-col items-center justify-end pb-20 pt-16"
                style={{ background: 'var(--gradient-primary)', minHeight: '20vh' }}
            >
                {/* Subtle wave bottom */}
                <div
                    className="pointer-events-none absolute bottom-0 left-0 right-0 h-12"
                    style={{
                        background: 'hsl(32 38% 97%)',
                        borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
                    }}
                />
                {/* Logo */}
                <div
                    className="relative flex h-[76px] w-[76px] items-center justify-center rounded-[22px] shadow-2xl mb-3"
                    style={{ background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(12px)', border: '1.5px solid rgba(255,255,255,0.35)' }}
                >
                    <img src="/logo-only.png" alt="Hadir" className="h-11 w-11 object-contain drop-shadow-sm" />
                </div>
                <h1 className="text-[26px] font-black text-white tracking-tight">{t.loginTitle}</h1>
                <p className="text-[13px] text-white/65 mt-0.5 font-medium">{t.loginSubtitle}</p>
            </div>

            {/* ── Form area ── */}
            <div className="flex-1 flex flex-col px-6 pb-10 max-w-sm w-full mx-auto animate-fade-in">

                {/* Heading */}
                <div className="mb-5">
                    <h2 className="text-[22px] font-black text-foreground leading-tight">{t.loginWelcome}</h2>
                    <p className="text-[13px] text-muted-foreground mt-1">{t.loginDesc}</p>
                </div>

                <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
                    {/* Error banner */}
                    {error && (
                        <div
                            className="flex items-start gap-2.5 p-3.5 rounded-2xl text-sm border"
                            style={{ background: 'hsl(var(--destructive) / 0.07)', borderColor: 'hsl(var(--destructive) / 0.2)', color: 'hsl(var(--destructive))' }}
                            data-testid="login-error"
                        >
                            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            <span className="text-[13px] font-medium">{error}</span>
                        </div>
                    )}

                    {/* Email/Username */}
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-extrabold text-muted-foreground uppercase tracking-widest">
                            {t.loginEmail}
                        </label>
                        <div className="relative">
                            <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60 pointer-events-none" />
                            <Input
                                id="emailOrUsername"
                                name="emailOrUsername"
                                type="text"
                                placeholder={t.loginEmailPlaceholder}
                                value={formData.emailOrUsername}
                                onChange={handleChange}
                                disabled={loading}
                                autoComplete="username"
                                data-testid="email-input"
                                className="pl-10 h-12 rounded-xl text-[14px] bg-white border-border/70 shadow-sm focus:border-primary/50 focus-visible:ring-1 focus-visible:ring-primary/30 transition-all"
                            />
                        </div>
                    </div>

                    {/* Password */}
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-extrabold text-muted-foreground uppercase tracking-widest">
                            {t.loginPassword}
                        </label>
                        <div className="relative">
                            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60 pointer-events-none" />
                            <Input
                                id="password"
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder={t.loginPasswordPlaceholder}
                                value={formData.password}
                                onChange={handleChange}
                                disabled={loading}
                                autoComplete="current-password"
                                data-testid="password-input"
                                className="pl-10 pr-11 h-12 rounded-xl text-[14px] bg-white border-border/70 shadow-sm focus:border-primary/50 focus-visible:ring-1 focus-visible:ring-primary/30 transition-all"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((p) => !p)}
                                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                                tabIndex={-1}
                            >
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full h-12 rounded-xl text-[15px] font-black text-white flex items-center justify-center gap-2 mt-1 transition-all active:scale-[0.98] disabled:opacity-60"
                        style={{ background: 'var(--gradient-primary)', boxShadow: '0 4px 18px rgba(217,90,20,0.38)' }}
                        data-testid="login-submit-btn"
                    >
                        {loading ? (
                            <><Loader2 className="h-4 w-4 animate-spin" />{t.loginSigningIn}</>
                        ) : (
                            <>{t.loginSignIn}<ArrowRight className="h-4 w-4" /></>
                        )}
                    </button>
                </form>

                {/* Footer */}
                <div className="mt-8 text-center text-[13px]">
                    <span className="text-muted-foreground">{t.loginNeedAccess} </span>
                    <Link to="/register" className="font-bold text-primary hover:opacity-80 transition-opacity">
                        {t.loginContactHR}
                    </Link>
                </div>

                <p className="mt-auto pt-8 text-center text-[11px] text-muted-foreground/60">
                    {t.developedBy}{' '}
                    <a href="https://eltera.id" target="_blank" rel="noopener noreferrer" className="font-bold hover:text-muted-foreground transition-colors">
                        Eltera
                    </a>
                </p>
            </div>
        </div>
    );
}
